import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { dbTreatmentPlans, dbSurgeries, type TreatmentPlan, type Surgery } from '../../lib/localDB';
import CalendarGrid, { buildDayEvents } from './components/CalendarGrid';
import DayDetailPanel from './components/DayDetailPanel';

const MONTHS_TR = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

// Appointments sayfasıyla aynı key — filtre oradan yönetilir
const APPOINTMENTS_CLINICS_KEY = 'appointments_selected_clinics';

export default function CalendarPage() {
  const navigate = useNavigate();
  const todayDate = new Date();
  const [currentYear, setCurrentYear] = useState(todayDate.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(todayDate.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [plans, setPlans] = useState<TreatmentPlan[]>([]);
  const [surgeries, setSurgeries] = useState<Surgery[]>([]);
  const [loading, setLoading] = useState(true);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [selectedClinics, setSelectedClinics] = useState<string[]>(() => {
    try {
      const s = localStorage.getItem(APPOINTMENTS_CLINICS_KEY);
      return s ? JSON.parse(s) : [];
    } catch { return []; }
  });

  const today = todayDate.toISOString().split('T')[0];

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Appointments sayfasındaki klinik seçimini senkron oku
  const syncClinics = () => {
    try {
      const s = localStorage.getItem(APPOINTMENTS_CLINICS_KEY);
      setSelectedClinics(s ? JSON.parse(s) : []);
    } catch { /* ignore */ }
  };

  const loadData = async () => {
    setLoading(true);
    syncClinics();
    try {
      const [allPlans, allSurgeries] = await Promise.all([
        dbTreatmentPlans.getAll(),
        dbSurgeries.getAll(),
      ]);
      setPlans(allPlans);
      setSurgeries(allSurgeries);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const surgeryClinicMap = useMemo(
    () => surgeries.map((s) => ({ id: s.id, clinic: s.clinic })),
    [surgeries]
  );

  const dayEventsMap = useMemo(
    () => buildDayEvents(plans, surgeryClinicMap, selectedClinics),
    [plans, surgeryClinicMap, selectedClinics]
  );

  const selectedEvents = useMemo(
    () => (selectedDate ? (dayEventsMap.get(selectedDate) ?? []) : []),
    [selectedDate, dayEventsMap]
  );

  // Month stats
  const monthStats = useMemo(() => {
    const pad = (n: number) => String(n).padStart(2, '0');
    const prefix = `${currentYear}-${pad(currentMonth + 1)}`;
    let total = 0;
    let active = 0;
    let completed = 0;
    let patients = new Set<string>();

    dayEventsMap.forEach((events, date) => {
      if (!date.startsWith(prefix)) return;
      events.forEach((ev) => {
        total++;
        patients.add(ev.patientName);
        if (ev.completed) completed++;
        else active++;
      });
    });

    return { total, active, completed, patients: patients.size };
  }, [dayEventsMap, currentYear, currentMonth]);

  const goToPrevMonth = () => {
    if (currentMonth === 0) { setCurrentYear((y) => y - 1); setCurrentMonth(11); }
    else setCurrentMonth((m) => m - 1);
    setSelectedDate(null);
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) { setCurrentYear((y) => y + 1); setCurrentMonth(0); }
    else setCurrentMonth((m) => m + 1);
    setSelectedDate(null);
  };

  const goToToday = () => {
    setCurrentYear(todayDate.getFullYear());
    setCurrentMonth(todayDate.getMonth());
    setSelectedDate(today);
  };

if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 text-sm">Takvim yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      {/* NAV */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
              <i className="ri-calendar-2-line text-2xl text-white"></i>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Tedavi Takvimi</h1>
              <p className="text-xs text-gray-500">Aylık takip planı görünümü</p>
            </div>
          </div>

          <div className="flex flex-1 items-center justify-end gap-3">
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-2">
              <button onClick={() => navigate('/appointments')} className="px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm">
                <i className="ri-list-check-2 text-lg"></i>Tedavi Takibi
              </button>
              <button onClick={() => navigate('/dashboard')} className="px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm">
                <i className="ri-layout-line text-lg"></i>Dashboard
              </button>
            </div>

            {/* Mobile Navigation Toggle */}
            <div className="md:hidden relative" ref={mobileMenuRef}>
              <button
                onClick={() => setShowMobileMenu((v) => !v)}
                className={`p-2 rounded-lg transition-all flex items-center justify-center border ${showMobileMenu ? 'bg-gray-100 border-gray-300 text-gray-900' : 'text-gray-700 hover:bg-gray-100 border-transparent'}`}
                title="Menü"
              >
                <i className={`${showMobileMenu ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
              
              {showMobileMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-xl overflow-hidden z-50 flex flex-col" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                  <div className="py-2 flex flex-col">
                    <button onClick={() => { navigate('/appointments'); setShowMobileMenu(false); }} className="w-full px-5 py-3 text-left text-sm font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50 cursor-pointer">
                      Tedavi Takibi
                    </button>
                    <button onClick={() => { navigate('/dashboard'); setShowMobileMenu(false); }} className="w-full px-5 py-3 text-left text-sm font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50 cursor-pointer">
                      Dashboard
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button onClick={() => navigate('/new-record')} className="px-3 py-2 md:px-5 md:py-2.5 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-700 transition-all flex items-center gap-1 md:gap-2 whitespace-nowrap cursor-pointer text-xs md:text-sm">
              <i className="ri-add-line text-base md:text-lg hidden md:block"></i>Yeni Kayıt
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Header row */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
          {/* Month navigation */}
          <div className="flex items-center gap-3">
            <button
              onClick={goToPrevMonth}
              className="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-colors cursor-pointer text-gray-600"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <h2 className="text-2xl font-bold text-gray-900 min-w-[200px] text-center">
              {MONTHS_TR[currentMonth]} {currentYear}
            </h2>
            <button
              onClick={goToNextMonth}
              className="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-colors cursor-pointer text-gray-600"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
            <button
              onClick={goToToday}
              className="px-4 py-2 text-sm font-medium text-teal-600 bg-teal-50 border border-teal-200 rounded-xl hover:bg-teal-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Bugün
            </button>
          </div>

          {/* Stats + Clinic info */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Month stats pills */}
            <div className="flex items-center gap-2">
              {[
                { label: 'Hasta', value: monthStats.patients, icon: 'ri-user-line', color: 'teal' },
                { label: 'Aktif', value: monthStats.active, icon: 'ri-time-line', color: 'amber' },
                { label: 'Tamam', value: monthStats.completed, icon: 'ri-check-line', color: 'green' },
              ].map((s) => (
                <div key={s.label} className={`flex items-center gap-1.5 px-3 py-1.5 bg-${s.color}-50 border border-${s.color}-200 rounded-xl`}>
                  <i className={`${s.icon} text-${s.color}-600 text-sm`}></i>
                  <span className={`text-sm font-bold text-${s.color}-700`}>{s.value}</span>
                  <span className={`text-xs text-${s.color}-600`}>{s.label}</span>
                </div>
              ))}
            </div>

            {/* Clinic filter info — managed from Appointments page */}
            <button
              onClick={() => navigate('/appointments')}
              className={`flex items-center gap-2 px-4 py-2 border rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap ${
                selectedClinics.length > 0
                  ? 'bg-teal-50 border-teal-300 text-teal-700 hover:bg-teal-100'
                  : 'bg-white border-gray-200 text-gray-500 hover:border-teal-300'
              }`}
            >
              <i className="ri-hospital-line text-teal-600"></i>
              {selectedClinics.length === 0
                ? 'Tüm Klinikler'
                : `${selectedClinics.length} Klinik Seçili`}
              <span className="text-xs opacity-70">· Tedavi Takibi'nden yönet</span>
              <i className="ri-arrow-right-s-line text-xs opacity-60"></i>
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="mb-4 flex flex-wrap items-center gap-3 bg-white rounded-xl px-5 py-3">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Adım Renkleri:</span>
          {[
            { color: 'bg-teal-500', label: 'Kontrol' },
            { color: 'bg-cyan-500', label: '2. Ay' },
            { color: 'bg-emerald-500', label: 'Healing' },
            { color: 'bg-amber-500', label: 'Prova' },
            { color: 'bg-orange-500', label: 'Teslim' },
            { color: 'bg-green-500', label: 'Final' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-1.5">
              <span className={`w-3 h-3 rounded-full ${item.color}`}></span>
              <span className="text-xs text-gray-600">{item.label}</span>
            </div>
          ))}
        </div>

        {/* Main content */}
        <div className="flex flex-col lg:flex-row gap-5">
          {/* Calendar */}
          <div className="flex-1 min-w-0">
            <CalendarGrid
              year={currentYear}
              month={currentMonth}
              plans={plans}
              clinics={selectedClinics}
              surgeries={surgeryClinicMap}
              selectedDate={selectedDate}
              onSelectDate={(d) => setSelectedDate(d === selectedDate ? null : d)}
              today={today}
            />
          </div>

          {/* Day detail panel */}
          <div className="w-full lg:w-[340px] flex-shrink-0">
            <DayDetailPanel
              date={selectedDate}
              events={selectedEvents}
              onClose={() => setSelectedDate(null)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
